import {generateElementKey} from '../common/action-flows/ids'
import {
  CardDefinition,
  SubmitButtonElement,
  UIElement,
  UIElementType,
  BannerElement,
  BannerType
} from '../common/types'

export const labelMap: {[t in UIElementType]: string} = {
  dismissButton: 'Dismiss',
  snoozeButton: 'Snooze',
  voteUpButton: 'Vote up',
  voteDownButton: 'Vote down',
  buttonGroup: 'Button group',
  checkbox: 'Checkbox',
  datePicker: 'Date picker',
  dropdown: 'Dropdown',
  headline: 'Headline',
  image: 'Image',
  imageList: 'Image list',
  linkButton: 'Link',
  list: 'List',
  numberInput: 'Number input',
  radioGroup: 'Radio group',
  submitButton: 'Submit',
  stepper: 'Stepper',
  switch: 'Switch',
  text: 'Text block',
  textInput: 'Text input',
  video: 'Video'
}

export const bannerLabelMap: {[t in BannerType]: string} = {
  image: 'Image banner',
  video: 'Video banner'
}

export const getDefaultName = (prefix: string) =>
  `${prefix}_${Math.random()
    .toString(36)
    .replace(/[^a-z]+/g, '')
    .substring(0, 5)}`

export const uiElementTypes = Object.keys(labelMap) as UIElementType[]

export const newUIElement = (type: UIElementType, id?: string): UIElement => {
  const key = id ?? generateElementKey(type)
  const name = getDefaultName(type)
  switch (type) {
    case 'checkbox':
      return {
        type,
        key,
        attributes: {name, text: '', checked: false}
      }
    case 'datePicker':
      return {
        type,
        key,
        attributes: {
          name,
          label: '',
          placeholder: 'Choose',
          format: 'inline'
        }
      }
    case 'dropdown':
      return {
        type,
        key,
        attributes: {
          name,
          label: '',
          placeholder: 'Choose',
          format: 'inline',
          source: ''
        }
      }
    case 'text':
    case 'headline':
      return {
        type,
        key,
        attributes: {text: ''}
      }
    case 'list':
      return {
        type,
        key,
        attributes: {type: 'bullet', source: ''}
      }
    case 'buttonGroup':
      return {
        type,
        key,
        attributes: {},
        children: []
      }
    case 'submitButton':
      return {
        type,
        key,
        attributes: {
          name,
          text: labelMap[type],
          appearance: 'primary',
          values: {},
          responseDefinition: ''
        }
      }
    case 'linkButton':
      return {
        type,
        key,
        attributes: {
          text: labelMap[type],
          appearance: 'primary',
          layoutId: '',
          name
        }
      }
    case 'voteUpButton':
    case 'voteDownButton':
    case 'dismissButton':
    case 'snoozeButton':
      return {
        type,
        key,
        attributes: {text: labelMap[type], appearance: 'primary', name}
      }
    case 'video':
    case 'image':
      return {
        type,
        key,
        attributes: {format: 'inline', src: ''}
      }
    case 'radioGroup':
      return {
        type,
        key,
        attributes: {name}
      }
    case 'stepper':
      return {
        type,
        key,
        attributes: {
          name,
          label: '',
          minimumValue: 1,
          maximumValue: 100,
          stepValue: 1,
          defaultValue: 1
        }
      }
    case 'switch':
      return {
        type,
        key,
        attributes: {
          name,
          label: '',
          defaultValue: true
        }
      }
    case 'textInput':
      return {
        type,
        key,
        attributes: {numberOfLines: 0, name, format: 'inline'}
      }
    case 'numberInput':
      return {
        type,
        key,
        attributes: {
          name
        }
      }
    default:
      throw new Error(`Unknown element type: ${type}`)
  }
}

export const newSubview = (title: string): CardDefinition['subviews'][0] => ({
  title,
  inputs: [],
  actions: [newUIElement('submitButton') as SubmitButtonElement]
})

export const newBannerElement = (
  type: BannerType,
  key?: string
): BannerElement => ({
  type,
  key: key ?? generateElementKey(type),
  attributes: {src: ''}
})
